import {
  Button,
  Link,
  LinkMd,
  mediaHelper,
  paletteHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { PcaLink } from '../../app-kit/components/PcaLink/PcaLink';
import { ClickThroughSourceLocalStorageValue } from '../../analytics/click-through-tracking/TrackingSource';
import { clickThroughSourceTracking } from '../../analytics/click-through-tracking/clickThroughSourceTracking';

const LinkCollectionWrapper = styled.div``;

const StyledLink = styled(Link)`
  ${typographyHelper('body01')};
  color: ${paletteHelper('textSecondary')};
  &:hover {
    color: ${paletteHelper('textPrimary')};
  }
  margin: ${spacingHelper('extraSmall 0')};
`;

const LinkContainer = styled.div.attrs<{ $isHidden?: boolean }>(({ $isHidden }) => {
  return $isHidden ? { 'aria-hidden': true } : {};
})`
  display: flex;
  flex-wrap: wrap;
  ${StyledLink} {
    padding-right: ${spacingHelper('medium')};
    flex-basis: 50%;
    ${mediaHelper('tabletMin')} {
      flex-basis: 33.33%;
    }
    ${mediaHelper('desktopMin')} {
      flex-basis: 25%;
    }
  }
  ${({ $isHidden }) =>
    $isHidden &&
    css`
      display: none;
    `}
`;

const ShowMoreButton = styled(Button)`
  ${typographyHelper('overline')};
  display: block;
  margin: ${spacingHelper('extraLarge auto medium')};
`;

export interface LinkItem {
  link: {
    title: string;
    url: string;
    clickThroughTracking?: ClickThroughSourceLocalStorageValue | null;
  };
}

interface LinkCollectionProps {
  visible: LinkItem[];
  hidden?: LinkItem[] | null;
  showMoreButtonText?: string | null;
}

export const LinkCollection: FunctionComponent<LinkCollectionProps> = ({
  visible,
  hidden,
  showMoreButtonText,
}) => {
  const [hideHiddenLinks, setHideHiddenLinks] = useState<boolean>(true);

  return (
    <LinkCollectionWrapper>
      <LinkContainer $isHidden={false}>
        {visible.map(({ link: { title, url, clickThroughTracking } }) => {
          return (
            <StyledLink
              key={url}
              icon={<LinkMd />}
              to={url}
              forwardedAs={PcaLink}
              onClick={() => clickThroughSourceTracking(clickThroughTracking ?? null)}
            >
              {title}
            </StyledLink>
          );
        })}
      </LinkContainer>
      {hidden && (
        <LinkContainer $isHidden={hideHiddenLinks}>
          {hidden.map(({ link: { title, url, clickThroughTracking } }) => {
            return (
              <StyledLink
                key={url}
                icon={<LinkMd />}
                to={url}
                forwardedAs={PcaLink}
                onClick={() => clickThroughSourceTracking(clickThroughTracking ?? null)}
              >
                {title}
              </StyledLink>
            );
          })}
        </LinkContainer>
      )}

      {hidden && showMoreButtonText && hideHiddenLinks && (
        <ShowMoreButton variant="link-primary" onClick={() => setHideHiddenLinks(false)}>
          {showMoreButtonText}
        </ShowMoreButton>
      )}
    </LinkCollectionWrapper>
  );
};
